import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Phone, MessageCircle, RefreshCw, Users, PhoneCall, FileText, Clock, X } from 'lucide-react';
import api from '../../../services/api';
import { toast } from 'sonner';
import useAuthStore from '../../../store/authStore';
import { openWhatsApp } from '../../../utils/whatsapp';

const OUTCOMES = [
  { v: 'connected', l: 'Connected' }, { v: 'call_back', l: 'Call Back' },
  { v: 'not_answering', l: 'Not Answering' }, { v: 'switched_off', l: 'Switched Off' },
  { v: 'not_interested', l: 'Not Interested' }, { v: 'not_qualified', l: 'Not Qualified' },
];
const STATUS_OPTS = [
  { v: '', l: 'No change' }, { v: 'leads', l: 'Lead' }, { v: 'follow_up', l: 'Follow Up' },
  { v: 'not_interested', l: 'Not Interested' }, { v: 'file', l: 'Convert to File' },
];
const fmtDur = (s) => `${String(Math.floor((s || 0) / 60)).padStart(2, '0')}:${String((s || 0) % 60).padStart(2, '0')}`;
const fmtT = (s) => { if (!s) return '0m'; const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60); return h ? `${h}h ${m}m` : `${m}m`; };

// IST-aware period bounds (mirrors backend _period_bounds) returned as epoch-ms [start, end).
// null start => lifetime (no lower bound). Used for CLIENT-SIDE Leads-tab filtering only.
const IST_OFFSET = 5.5 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
const istBounds = (period, fromDate, toDate) => {
  if (fromDate && toDate) {
    const s = Date.parse(`${fromDate}T00:00:00+05:30`);
    const e = Date.parse(`${toDate}T00:00:00+05:30`) + DAY_MS;
    return [s, e];
  }
  const nowIst = new Date(Date.now() + IST_OFFSET); // UTC getters now read IST wall-clock
  const y = nowIst.getUTCFullYear(), m = nowIst.getUTCMonth(), d = nowIst.getUTCDate();
  const weekdayMon = (nowIst.getUTCDay() + 6) % 7; // Mon=0
  const istMidnight = (yy, mm, dd) => Date.UTC(yy, mm, dd) - IST_OFFSET;
  const todayS = istMidnight(y, m, d);
  switch (period) {
    case 'today': return [todayS, todayS + DAY_MS];
    case 'yesterday': return [todayS - DAY_MS, todayS];
    case 'week': return [todayS - weekdayMon * DAY_MS, todayS + DAY_MS];
    case 'month': return [istMidnight(y, m, 1), todayS + DAY_MS];
    default: return [null, null]; // lifetime / custom handled above
  }
};

const Stat = ({ label, value, color = '#0f172a' }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-3 text-center" data-testid={`tl-stat-${label}`}>
    <p className="text-2xl font-bold" style={{ color }}>{value}</p>
    <p className="text-[11px] text-gray-500 mt-1">{label}</p>
  </div>
);

function TLCallModal({ lead, agentName, onClose, onSaved }) {
  const [seconds, setSeconds] = useState(0);
  const [outcome, setOutcome] = useState('');
  const [status, setStatus] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [fd, setFd] = useState(''); const [ft, setFt] = useState('');
  const [saving, setSaving] = useState(false);
  const start = useRef(Date.now());
  const boundRef = useRef(lead);
  useEffect(() => { const t = setInterval(() => setSeconds(Math.floor((Date.now() - start.current) / 1000)), 1000); return () => clearInterval(t); }, []);
  const needFollow = ['call_back', 'not_answering', 'switched_off'].includes(outcome);
  const submit = async () => {
    if (!outcome) { toast.error('Select a call outcome'); return; }
    if (outcome === 'not_qualified' && !reason.trim()) { toast.error('Reason required'); return; }
    if (boundRef.current.lead_id !== lead.lead_id) { toast.error('Call context changed'); onClose(); return; }
    setSaving(true);
    try {
      await api.post(`/tl/leads/${lead.lead_id}/call`, {
        duration_seconds: Math.floor((Date.now() - start.current) / 1000),
        outcome, resulting_status: status || null, reason: reason.trim() || null,
        notes: notes.trim() || null, follow_up_date: needFollow ? (fd || null) : null,
        follow_up_time: needFollow ? (ft || null) : null, convert_to_file: status === 'file',
      });
      toast.success('TL call logged'); onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to log call'); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="tl-call-modal">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center animate-pulse"><PhoneCall size={20} /></div>
            <div><p className="text-sm font-semibold text-gray-800">TL Call · {lead.name}</p><p className="text-xs text-gray-500 flex items-center gap-1"><Clock size={12} /> {fmtDur(seconds)}</p></div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <p className="text-[11px] text-indigo-600 bg-indigo-50 rounded px-2 py-1 mb-3">Second-level TL call — original GP: <b>{lead.gp_name}</b> (ownership stays with GP)</p>
        <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Call Outcome</label>
        <select data-testid="tl-outcome-select" value={outcome} onChange={(e) => setOutcome(e.target.value)} className="mt-1 mb-3 w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white outline-none">
          <option value="">Select outcome...</option>{OUTCOMES.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
        <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Update Status</label>
        <select data-testid="tl-status-select" value={status} onChange={(e) => setStatus(e.target.value)} className="mt-1 mb-3 w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white outline-none">
          {STATUS_OPTS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
        {outcome === 'not_qualified' && <input data-testid="tl-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)" className="mb-3 w-full border border-gray-300 rounded-md px-3 py-2 text-sm outline-none" />}
        {needFollow && (
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div><label className="text-xs text-gray-500">Follow-up Date</label><input type="date" data-testid="tl-fu-date" value={fd} onChange={(e) => setFd(e.target.value)} className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm" /></div>
            <div><label className="text-xs text-gray-500">Follow-up Time</label><input type="time" data-testid="tl-fu-time" value={ft} onChange={(e) => setFt(e.target.value)} className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm" /></div>
          </div>
        )}
        <textarea data-testid="tl-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Notes / next step..." className="mb-4 w-full border border-gray-300 rounded-md px-3 py-2 text-sm outline-none resize-none" />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="border border-gray-200 text-gray-600 hover:bg-gray-50 rounded-md px-4 py-2 text-sm font-medium">Cancel</button>
          <button data-testid="tl-save-call" disabled={saving} onClick={submit} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-md px-4 py-2 text-sm font-medium disabled:opacity-60">Save Call</button>
        </div>
      </div>
    </div>
  );
}

const TeamLeads = () => {
  const { user } = useAuthStore();
  const [meta, setMeta] = useState({ tls: [], gps: [], is_tl: false, me: null, outcomes: [] });
  const [tab, setTab] = useState('leads');
  const [period, setPeriod] = useState('month');
  const [fromDate, setFromDate] = useState(''); const [toDate, setToDate] = useState('');
  const [tl, setTl] = useState('ALL'); const [gp, setGp] = useState('ALL');
  const [status, setStatus] = useState('ALL'); const [outcome, setOutcome] = useState('ALL'); const [converted, setConverted] = useState('ALL');
  const [callLog, setCallLog] = useState([]);
  const [summary, setSummary] = useState(null);
  const [hourly, setHourly] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allLeads, setAllLeads] = useState([]); // full TL-assigned set; Leads tab filters it client-side
  const [callLead, setCallLead] = useState(null);
  const callRef = useRef(null);

  const rangeParams = useCallback(() => {
    const p = {};
    if (fromDate && toDate) { p.from_date = fromDate; p.to_date = toDate; } else { p.period = period; }
    if (tl !== 'ALL') p.tl = tl; if (gp !== 'ALL') p.gp = gp;
    return p;
  }, [period, fromDate, toDate, tl, gp]);

  useEffect(() => { api.get('/tl/meta').then(({ data }) => { setMeta(data); if (data.is_tl && data.me) setTl(data.me); }).catch(() => {}); }, []);

  // Full TL-assigned lead set. period=lifetime => backend applies NO date filter and NO
  // tl-contacted filter, so we get EVERY assigned lead and do all Leads-tab filtering client-side.
  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      // When a specific TL is selected we ask the backend to scope the pool to that TL's
      // canonical team (tl_team) so Admin sees the SAME complete population as the TL's own
      // login. For "All Team Leaders" we fetch the full scoped set and bucket client-side.
      const params = { period: 'lifetime' };
      if (tl !== 'ALL') params.tl_team = tl;
      const { data } = await api.get('/tl/leads', { params });
      setAllLeads(data.leads || []);
    } catch (e) { /* interceptor handles */ } finally { setLoading(false); }
  }, [tl]);
  useEffect(() => { loadLeads(); }, [loadLeads]);

  // Secondary tabs keep their existing backend endpoints unchanged.
  const loadTab = useCallback(async () => {
    const rp = rangeParams();
    try {
      if (tab === 'calllog') setCallLog((await api.get('/tl/call-logs', { params: { ...rp, outcome, status, converted } })).data.logs || []);
      if (tab === 'summary') setSummary((await api.get('/tl/reports/summary', { params: rp })).data);
      if (tab === 'hourly') setHourly((await api.get('/tl/reports/hourly', { params: { date: (toDate || undefined), tl: tl !== 'ALL' ? tl : undefined } })).data);
    } catch (e) { /* interceptor handles */ }
  }, [rangeParams, outcome, status, converted, tab, tl, toDate]);
  useEffect(() => { loadTab(); }, [loadTab]);

  const load = useCallback(() => { loadLeads(); loadTab(); }, [loadLeads, loadTab]);
  const openCall = (lead) => { callRef.current = lead; setCallLead(lead); };
  const periods = [['today', 'Today'], ['yesterday', 'Yesterday'], ['week', 'This Week'], ['month', 'This Month']];

  // Scope GP dropdown to the selected Team Leader (fallback to all if hierarchy data missing)
  const scopedGps = React.useMemo(() => {
    if (tl === 'ALL') return meta.gps;
    const under = meta.gps.filter((g) => g.tl_id && String(g.tl_id) === String(tl));
    return under.length ? under : meta.gps;
  }, [meta.gps, tl]);
  useEffect(() => {
    if (gp !== 'ALL' && !scopedGps.some((g) => g.id === gp)) setGp('ALL');
  }, [scopedGps]); // eslint-disable-line react-hooks/exhaustive-deps

  // GP -> Team Leader map keyed by NAME. Lead.gp_id and /tl/meta gp.id use different id
  // variants for the same GP, so we join on gp_name (reliable) instead of gp_id.
  const norm = (s) => (s || '').trim().toLowerCase();
  const nameToTl = React.useMemo(() => {
    const m = {};
    (meta.gps || []).forEach((g) => { m[norm(g.name)] = g.tl_id ? String(g.tl_id) : ''; });
    return m;
  }, [meta.gps]);
  const gpIdToName = React.useMemo(() => {
    const m = {};
    (meta.gps || []).forEach((g) => { m[String(g.id)] = norm(g.name); });
    return m;
  }, [meta.gps]);

  // ===== CLIENT-SIDE Leads-tab derivation (base = ALL assigned leads; NOT TL-contacted) =====
  const derived = React.useMemo(() => {
    let scoped = allLeads;
    // TL scope is enforced SERVER-SIDE via tl_team (see loadLeads), so the pool already contains
    // exactly the selected TL's team - no fragile client-side gp_name->tl re-filtering needed.
    if (gp !== 'ALL') { const gn = gpIdToName[String(gp)]; scoped = scoped.filter((l) => norm(l.gp_name) === gn); }

    const isFile = (l) => (l.status || '').toLowerCase() === 'file';
    const inB = (l, b) => {
      if (!b[0]) return true;
      const t = Date.parse(l.lead_created_at);
      return !isNaN(t) && t >= b[0] && t < b[1];
    };
    const selB = istBounds((fromDate && toDate) ? null : period, fromDate, toDate);
    const inRangeScoped = scoped.filter((l) => inB(l, selB)); // includes FILE (needed for Converted)

    const outMatch = (l) => outcome === 'ALL' || (l.last_tl_call && l.last_tl_call.outcome === outcome);
    const convMatch = (l) => converted === 'ALL' || (converted === 'yes' ? !!l.converted_by_tl : !l.converted_by_tl);
    // Visible cards = assigned leads in range, FILE excluded, then explicit outcome/converted filters.
    const visible = inRangeScoped.filter((l) => !isFile(l) && outMatch(l) && convMatch(l));

    const countNonFile = (b) => scoped.filter((l) => !isFile(l) && inB(l, b)).length;
    const rangeTotal = visible.length;
    const contacted = visible.filter((l) => (l.tl_calls_count || 0) > 0).length;
    const filesConverted = inRangeScoped.filter((l) => l.converted_by_tl).length;
    const base = rangeTotal + filesConverted;
    return {
      list: visible,
      kpis: {
        range_total_leads: rangeTotal,
        today: countNonFile(istBounds('today')),
        week: countNonFile(istBounds('week')),
        month: countNonFile(istBounds('month')),
        contacted_leads: contacted,
        files_converted: filesConverted,
        pending: Math.max(0, rangeTotal - contacted),
        file_conversion_rate: base ? Math.round((1000 * filesConverted) / base) / 10 : 0,
      },
    };
  }, [allLeads, tl, gp, nameToTl, gpIdToName, period, fromDate, toDate, outcome, converted]);
  const stats = derived.kpis;
  const leads = derived.list;

  return (
    <div className="p-4" data-testid="tl-team-leads">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-gray-900">Team Leads (TL)</h2>
        <button onClick={load} className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50" data-testid="tl-refresh"><RefreshCw size={18} className={loading ? 'animate-spin' : ''} /></button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
        {[['leads', 'Leads'], ['calllog', 'TL Call Log'], ['summary', 'TL Summary'], ['hourly', 'TL Hourly']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tl-tab-${k}`}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${tab === k ? 'bg-emerald-600 text-white' : 'bg-white text-gray-700 border border-gray-200'}`}>{l}</button>
        ))}
      </div>

      {/* Filters */}
      <div className="space-y-3 mb-4">
        {/* Date presets + custom range */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-gray-500 mr-1">Date:</span>
          {periods.map(([k, l]) => (
            <button key={k} onClick={() => { setFromDate(''); setToDate(''); setPeriod(k); }}
              data-testid={`tl-period-${k}`}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${period === k && !fromDate ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}>{l}</button>
          ))}
          <div className="flex items-center gap-1.5 ml-auto flex-wrap">
            <label className="text-[11px] text-gray-500">From</label>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="border border-gray-300 rounded-md px-2 py-1.5 text-xs" data-testid="tl-from" />
            <label className="text-[11px] text-gray-500">To</label>
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="border border-gray-300 rounded-md px-2 py-1.5 text-xs" data-testid="tl-to" />
          </div>
        </div>
        {/* Dropdown filters (wrap cleanly, full-width on mobile) */}
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
          {(meta.tls.length > 1 || !meta.is_tl) && (
            <select value={tl} onChange={(e) => setTl(e.target.value)} className="w-full sm:w-auto sm:min-w-[160px] border border-gray-300 rounded-md px-2 py-2 text-sm bg-white" data-testid="tl-filter-tl">
              <option value="ALL">All Team Leaders</option>{meta.tls.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          )}
          <select value={gp} onChange={(e) => setGp(e.target.value)} className="w-full sm:w-auto sm:min-w-[160px] border border-gray-300 rounded-md px-2 py-2 text-sm bg-white" data-testid="tl-filter-gp">
            <option value="ALL">All Growth Partners</option>{scopedGps.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
          <select value={outcome} onChange={(e) => setOutcome(e.target.value)} className="w-full sm:w-auto sm:min-w-[140px] border border-gray-300 rounded-md px-2 py-2 text-sm bg-white" data-testid="tl-filter-outcome">
            <option value="ALL">Any outcome</option>{OUTCOMES.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
          <select value={converted} onChange={(e) => setConverted(e.target.value)} className="w-full sm:w-auto sm:min-w-[150px] border border-gray-300 rounded-md px-2 py-2 text-sm bg-white" data-testid="tl-filter-conv">
            <option value="ALL">All</option><option value="yes">TL-converted to File</option><option value="no">Not converted</option>
          </select>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-8 gap-2 mb-5">
          <Stat label="Leads (range)" value={stats.range_total_leads} color="#2563eb" />
          <Stat label="Today" value={stats.today} />
          <Stat label="This Week" value={stats.week} />
          <Stat label="This Month" value={stats.month} />
          <Stat label="TL Contacted" value={stats.contacted_leads} color="#7c3aed" />
          <Stat label="Converted" value={stats.files_converted} color="#ea580c" />
          <Stat label="Pending" value={stats.pending} color="#dc2626" />
          <Stat label="Conv %" value={`${stats.file_conversion_rate}%`} color="#059669" />
        </div>
      )}

      {/* LEADS TAB */}
      {tab === 'leads' && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="tl-leads-grid">
          {leads.length === 0 ? <p className="text-gray-400 text-sm py-8 text-center col-span-full">No leads in this range</p> : leads.map((l) => (
            <div key={l.lead_id} className="bg-white border border-gray-200 rounded-lg p-4" data-testid={`tl-lead-${l.lead_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0"><p className="font-semibold text-gray-900 truncate">{l.name}</p><p className="text-xs text-gray-500">{l.phone || '—'}</p></div>
                <span className={`text-[10px] font-bold px-2 py-1 rounded ${l.status === 'file' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>{(l.status || '').toUpperCase()}</span>
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-2 text-xs">
                <span className="text-gray-400">Loan</span><span className="text-gray-700 truncate">{l.loan_type || 'loan requirement'}</span>
                <span className="text-gray-400">Original GP</span><span className="text-gray-700 truncate">{l.gp_name}</span>
                <span className="text-gray-400">Became Lead</span><span className="text-gray-700">{l.lead_created_at ? new Date(l.lead_created_at).toLocaleDateString() : '—'}</span>
                <span className="text-gray-400">Last GP Call</span><span className="text-gray-700">{l.last_call_outcome || '—'}</span>
                <span className="text-gray-400">Last TL Call</span><span className="text-gray-700 truncate">{l.last_tl_call ? `${l.last_tl_call.outcome}` : '—'}</span>
                <span className="text-gray-400">Follow-up</span><span className="text-gray-700">{l.follow_up_date || '—'} {l.follow_up_time || ''}</span>
              </div>
              {l.converted_by_tl && <p className="text-[10px] text-orange-600 mt-2">TL-converted to File (owner remains GP)</p>}
              <div className="flex gap-2 mt-3">
                <a href={`tel:${l.phone}`} onClick={() => openCall(l)} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-md bg-emerald-600 text-white px-3 py-2 text-sm font-medium" data-testid={`tl-call-${l.lead_id}`}><Phone size={15} /> Call</a>
                <button type="button" onClick={() => openWhatsApp(l.phone, l.name, user?.name || 'Team')} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-md bg-green-500 text-white px-3 py-2 text-sm font-medium" data-testid={`tl-wa-${l.lead_id}`}><MessageCircle size={15} /> WhatsApp</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* CALL LOG TAB */}
      {tab === 'calllog' && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto" data-testid="tl-calllog">
          <table className="w-full text-sm">
            <thead><tr className="bg-gray-50 text-left text-xs text-gray-500">
              {['TL', 'Customer', 'Original GP', 'When', 'Dur', 'Outcome', 'Status', 'File'].map((h) => <th key={h} className="py-2 px-3">{h}</th>)}
            </tr></thead>
            <tbody>
              {callLog.length === 0 ? <tr><td colSpan={8} className="py-8 text-center text-gray-400">No TL calls</td></tr> : callLog.map((c) => (
                <tr key={c.tl_call_id} className="border-t border-gray-100">
                  <td className="py-2 px-3 font-medium">{c.tl_name}</td><td className="py-2 px-3">{c.customer}<div className="text-xs text-gray-400">{c.phone}</div></td>
                  <td className="py-2 px-3">{c.gp_name}</td><td className="py-2 px-3 text-xs">{c.at ? new Date(c.at).toLocaleString() : ''}</td>
                  <td className="py-2 px-3">{fmtDur(c.duration_seconds)}</td><td className="py-2 px-3">{c.outcome}</td>
                  <td className="py-2 px-3">{c.resulting_status || '—'}</td><td className="py-2 px-3">{c.converted_to_file ? '✓' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* SUMMARY TAB */}
      {tab === 'summary' && summary && (
        <div>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-4">
            <Stat label="TL Calls" value={summary.overall.tl_calls} color="#2563eb" />
            <Stat label="Connected" value={summary.overall.connected} color="#7c3aed" />
            <Stat label="Follow-ups" value={summary.overall.follow_ups} />
            <Stat label="Files" value={summary.overall.files} color="#ea580c" />
            <Stat label="Talk" value={fmtT(summary.overall.talk_seconds)} />
            <Stat label="Conv %" value={`${summary.overall.conversion_pct}%`} color="#059669" />
          </div>
          <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-50 text-left text-xs text-gray-500">{['Team Leader', 'Calls', 'Connected', 'Call Back', 'NA', 'Not Int.', 'Follow-ups', 'Files', 'Conv %'].map((h) => <th key={h} className="py-2 px-3">{h}</th>)}</tr></thead>
              <tbody>{summary.tls.map((t) => (
                <tr key={t.tl_id} className="border-t border-gray-100"><td className="py-2 px-3 font-medium">{t.tl_name}</td>
                  <td className="py-2 px-3">{t.tl_calls}</td><td className="py-2 px-3">{t.connected}</td><td className="py-2 px-3">{t.call_back}</td>
                  <td className="py-2 px-3">{t.not_answering}</td><td className="py-2 px-3">{t.not_interested}</td><td className="py-2 px-3">{t.follow_ups}</td>
                  <td className="py-2 px-3 text-orange-600 font-semibold">{t.files}</td><td className="py-2 px-3 text-emerald-600 font-semibold">{t.conversion_pct}%</td></tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}

      {/* HOURLY TAB */}
      {tab === 'hourly' && hourly && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto" data-testid="tl-hourly">
          <div className="px-4 py-3 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white text-sm font-semibold">TL Second-Level Hourly · {hourly.date}</div>
          <table className="w-full text-sm">
            <thead><tr className="bg-gray-50 text-left text-xs text-gray-500">{['Hour', 'TL Calls', 'Connected', 'Call Back', 'NA', 'Files'].map((h) => <th key={h} className="py-2 px-3">{h}</th>)}</tr></thead>
            <tbody>{hourly.hours.length === 0 ? <tr><td colSpan={6} className="py-8 text-center text-gray-400">No TL calls</td></tr> : hourly.hours.map((h) => (
              <tr key={h.hour} className="border-t border-gray-100"><td className="py-2 px-3 font-medium">{String(h.hour).padStart(2, '0')}:00</td>
                <td className="py-2 px-3">{h.tl_calls}</td><td className="py-2 px-3">{h.connected}</td><td className="py-2 px-3">{h.call_back}</td>
                <td className="py-2 px-3">{h.not_answering}</td><td className="py-2 px-3 text-orange-600">{h.files}</td></tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {callLead && <TLCallModal lead={callLead} agentName={user?.name} onClose={() => setCallLead(null)} onSaved={load} />}
    </div>
  );
};

export default TeamLeads;
